import React from "react";

// Cyberpunk Color Palette:
// Neon Cyan: #00f3ff
// Electric Purple: #9d00ff
// Cyber Lime: #00ff66
// Neon Pink: #ff0055
// Warm Amber: #ffaa00

export const IconContainer = ({ children, glowColor = "#00f3ff", className = "", size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={{
      filter: `drop-shadow(0px 0px 4px ${glowColor}80)`,
      overflow: "visible"
    }}
  >
    {children}
  </svg>
);

// 1. Candlestick Chart (Trading/Markets)
export const CandlestickIcon = ({ size = 24, glow = "#00f3ff" }) => (
  <IconContainer size={size} glowColor={glow}>
    {/* Grid backdrop accents */}
    <path d="M2 19H22" stroke="#00f3ff" strokeWidth="1.2" strokeOpacity="0.4" strokeDasharray="2 2" />
    <path d="M2 5H22" stroke="#00f3ff" strokeWidth="1.2" strokeOpacity="0.3" strokeDasharray="2 2" />
    {/* Bullish Candle */}
    <line x1="7" y1="3" x2="7" y2="21" stroke="#00ff66" strokeWidth="1.5" strokeLinecap="round" />
    <rect x="5" y="7" width="4" height="8" rx="1" fill="#00ff66" fillOpacity="0.25" stroke="#00ff66" strokeWidth="1.5" />
    {/* Bearish Candle */}
    <line x1="17" y1="3" x2="17" y2="21" stroke="#ff0055" strokeWidth="1.5" strokeLinecap="round" />
    <rect x="15" y="10" width="4" height="7" rx="1" fill="#ff0055" fillOpacity="0.25" stroke="#ff0055" strokeWidth="1.5" />
    {/* Middle Candle */}
    <line x1="12" y1="5" x2="12" y2="18" stroke="#00f3ff" strokeWidth="1.5" strokeLinecap="round" />
    <rect x="10" y="8" width="4" height="5" rx="1" fill="#00f3ff" fillOpacity="0.3" stroke="#00f3ff" strokeWidth="1.5" />
  </IconContainer>
);

// 2. Order Book (L2 Depth)
export const OrderBookIcon = ({ size = 24, glow = "#9d00ff" }) => (
  <IconContainer size={size} glowColor={glow}>
    {/* Asks (Sell - Red/Pink) */}
    <rect x="3" y="4" width="10" height="3" rx="1" fill="#ff0055" fillOpacity="0.8" />
    <rect x="3" y="8" width="14" height="3" rx="1" fill="#ff0055" fillOpacity="0.5" />
    {/* Divider Line */}
    <line x1="3" y1="12" x2="21" y2="12" stroke="#9d00ff" strokeWidth="1.5" strokeDasharray="1 1" />
    {/* Bids (Buy - Green) */}
    <rect x="3" y="13" width="16" height="3" rx="1" fill="#00ff66" fillOpacity="0.5" />
    <rect x="3" y="17" width="11" height="3" rx="1" fill="#00ff66" fillOpacity="0.8" />
    {/* Outer Tech Frame */}
    <path d="M21 3V21" stroke="#9d00ff" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="21" cy="12" r="1.5" fill="#00f3ff" />
  </IconContainer>
);

// 3. Brain / AI Council (AI Decision Engine)
export const BrainCouncilIcon = ({ size = 24, glow = "#9d00ff" }) => (
  <IconContainer size={size} glowColor={glow}>
    <path
      d="M12 3C8 3 5 5.5 5 9.5C5 12 6.5 14 8.5 15.2C8.5 17 9.5 19 12 21C14.5 19 15.5 17 15.5 15.2C17.5 14 19 12 19 9.5C19 5.5 16 3 12 3Z"
      stroke="#9d00ff"
      strokeWidth="1.5"
      fill="#9d00ff"
      fillOpacity="0.15"
    />
    {/* Neural Circuit Lines */}
    <path d="M12 3V9M12 9L9 12M12 9L15 12M8.5 15.2L12 13M15.5 15.2L12 13" stroke="#00f3ff" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="9" r="1.5" fill="#00f3ff" />
    <circle cx="9" cy="12" r="1.2" fill="#00ff66" />
    <circle cx="15" cy="12" r="1.2" fill="#00ff66" />
    <circle cx="12" cy="13" r="1.2" fill="#ff0055" />
  </IconContainer>
);

// 4. Shield / Guardrail (Risk Management)
export const ShieldGuardIcon = ({ size = 24, glow = "#00ff66" }) => (
  <IconContainer size={size} glowColor={glow}>
    <path
      d="M12 2L4 5.5V11C4 16.5 7.5 20.5 12 22C16.5 20.5 20 16.5 20 11V5.5L12 2Z"
      stroke="#00ff66"
      strokeWidth="1.5"
      fill="#00ff66"
      fillOpacity="0.15"
      strokeLinejoin="round"
    />
    <path d="M12 7V13" stroke="#00f3ff" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="16" r="1" fill="#00f3ff" />
  </IconContainer>
);

// 5. Zap / Flash Execution (Speed/Latency)
export const ZapExecutionIcon = ({ size = 24, glow = "#ffaa00" }) => (
  <IconContainer size={size} glowColor={glow}>
    <path
      d="M13 2L4 13H11L9 22L20 9H12L13 2Z"
      stroke="#ffaa00"
      strokeWidth="1.5"
      fill="#ffaa00"
      fillOpacity="0.25"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="11" r="9" stroke="#00f3ff" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
  </IconContainer>
);

// 6. Activity Log / Terminal
export const ActivityLogIcon = ({ size = 24, glow = "#00f3ff" }) => (
  <IconContainer size={size} glowColor={glow}>
    <rect x="3" y="4" width="18" height="16" rx="2" stroke="#00f3ff" strokeWidth="1.5" fill="#00f3ff" fillOpacity="0.1" />
    <line x1="3" y1="8" x2="21" y2="8" stroke="#00f3ff" strokeWidth="1.2" />
    <circle cx="6" cy="6" r="0.8" fill="#ff0055" />
    <circle cx="8.5" cy="6" r="0.8" fill="#ffaa00" />
    <circle cx="11" cy="6" r="0.8" fill="#00ff66" />
    {/* Terminal Code Lines */}
    <path d="M6 12L8.5 14.5L6 17" stroke="#00ff66" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="11" y1="17" x2="17" y2="17" stroke="#00f3ff" strokeWidth="1.5" strokeLinecap="round" />
  </IconContainer>
);

// 7. News / Market Feed
export const NewsFeedIcon = ({ size = 24, glow = "#00f3ff" }) => (
  <IconContainer size={size} glowColor={glow}>
    <path d="M4 6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6Z" stroke="#00f3ff" strokeWidth="1.5" fill="#00f3ff" fillOpacity="0.1" />
    <path d="M8 9H16M8 13H16M8 16H13" stroke="#00f3ff" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="17" cy="16" r="1.5" fill="#9d00ff" />
  </IconContainer>
);

// 8. Settings / Config
export const SettingsConfigIcon = ({ size = 24, glow = "#9d00ff" }) => (
  <IconContainer size={size} glowColor={glow}>
    <polygon points="12 2 15 4 19 4 20 8 22 12 20 16 19 20 15 20 12 22 9 20 5 20 4 16 2 12 4 8 5 4 9 4" stroke="#9d00ff" strokeWidth="1.5" fill="#9d00ff" fillOpacity="0.15" />
    <circle cx="12" cy="12" r="3.5" stroke="#00f3ff" strokeWidth="1.5" fill="#00f3ff" fillOpacity="0.2" />
  </IconContainer>
);

// 9. Wallet / Capital
export const WalletCapitalIcon = ({ size = 24, glow = "#00ff66" }) => (
  <IconContainer size={size} glowColor={glow}>
    <rect x="2" y="6" width="20" height="14" rx="2" stroke="#00ff66" strokeWidth="1.5" fill="#00ff66" fillOpacity="0.1" />
    <path d="M2 10H22" stroke="#00ff66" strokeWidth="1.2" />
    <rect x="14" y="12" width="6" height="5" rx="1" fill="#ffaa00" fillOpacity="0.8" stroke="#ffaa00" strokeWidth="1" />
    <circle cx="17" cy="14.5" r="0.8" fill="#111" />
  </IconContainer>
);

// 10. Target / Signal
export const TargetSignalIcon = ({ size = 24, glow = "#ff0055" }) => (
  <IconContainer size={size} glowColor={glow}>
    <circle cx="12" cy="12" r="8" stroke="#ff0055" strokeWidth="1.5" fill="#ff0055" fillOpacity="0.1" />
    <circle cx="12" cy="12" r="3" stroke="#00f3ff" strokeWidth="1.5" />
    <line x1="12" y1="2" x2="12" y2="6" stroke="#ff0055" strokeWidth="1.5" />
    <line x1="12" y1="18" x2="12" y2="22" stroke="#ff0055" strokeWidth="1.5" />
    <line x1="2" y1="12" x2="6" y2="12" stroke="#ff0055" strokeWidth="1.5" />
    <line x1="18" y1="12" x2="22" y2="12" stroke="#ff0055" strokeWidth="1.5" />
  </IconContainer>
);

// 11. Trend Up / Profit
export const TrendUpIcon = ({ size = 24, glow = "#00ff66" }) => (
  <IconContainer size={size} glowColor={glow}>
    <path d="M3 17L9 11L13 15L21 6" stroke="#00ff66" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 6H21V12" stroke="#00ff66" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 21H21" stroke="#00f3ff" strokeWidth="1" strokeDasharray="2 2" opacity="0.4" />
  </IconContainer>
);

// 12. Trend Down / Loss
export const TrendDownIcon = ({ size = 24, glow = "#ff0055" }) => (
  <IconContainer size={size} glowColor={glow}>
    <path d="M3 7L9 13L13 9L21 18" stroke="#ff0055" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 18H21V12" stroke="#ff0055" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 21H21" stroke="#00f3ff" strokeWidth="1" strokeDasharray="2 2" opacity="0.4" />
  </IconContainer>
);

// 13. Flask / Backtest Lab
export const FlaskLabIcon = ({ size = 24, glow = "#00f3ff" }) => (
  <IconContainer size={size} glowColor={glow}>
    <path
      d="M9 3H15M10 3V8.5L4.5 18C3.8 19.2 4.7 21 6.1 21H17.9C19.3 21 20.2 19.2 19.5 18L14 8.5V3"
      stroke="#00f3ff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="#00f3ff"
      fillOpacity="0.12"
    />
    <path d="M7 14H17" stroke="#00ff66" strokeWidth="1.5" strokeDasharray="2 2" />
    <circle cx="10" cy="17" r="1" fill="#ffaa00" />
    <circle cx="14" cy="16" r="1.2" fill="#00ff66" />
  </IconContainer>
);

// Cyberpunk Icon Showcase Component
export function CyberpunkIconShowcase() {
  const icons = [
    { name: "Candlestick Chart", tag: "Trading/Markets", component: <CandlestickIcon size={32} /> },
    { name: "Order Book", tag: "L2 Depth", component: <OrderBookIcon size={32} /> },
    { name: "AI Council", tag: "Decision Engine", component: <BrainCouncilIcon size={32} /> },
    { name: "Risk Guardrail", tag: "Protection", component: <ShieldGuardIcon size={32} /> },
    { name: "Flash Execution", tag: "Latency / Speed", component: <ZapExecutionIcon size={32} /> },
    { name: "Activity Log", tag: "Terminal Events", component: <ActivityLogIcon size={32} /> },
    { name: "Market News", tag: "Macro Feed", component: <NewsFeedIcon size={32} /> },
    { name: "System Config", tag: "Settings", component: <SettingsConfigIcon size={32} /> },
    { name: "Wallet Balance", tag: "Capital", component: <WalletCapitalIcon size={32} /> },
    { name: "Target Signal", tag: "Strategy Reticle", component: <TargetSignalIcon size={32} /> },
    { name: "Bullish Trend", tag: "Profit", component: <TrendUpIcon size={32} /> },
    { name: "Bearish Trend", tag: "Risk", component: <TrendDownIcon size={32} /> },
    { name: "Backtest Lab", tag: "Simulation Engine", component: <FlaskLabIcon size={32} /> },
  ];

  return (
    <div style={{ padding: "24px", background: "#0a0c10", color: "#e1e7ef", borderRadius: "12px" }}>
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ color: "#00f3ff", fontFamily: "monospace", margin: 0 }}>⚡ CYBERPUNK 2077 ICON SUITE</h2>
        <p style={{ color: "#8b949e", fontSize: "13px", margin: "4px 0 0 0" }}>
          Vector-glowing HUD icons for high-frequency trading terminals.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: "16px",
        }}
      >
        {icons.map((item) => (
          <div
            key={item.name}
            style={{
              background: "#12161f",
              border: "1px solid rgba(0, 243, 255, 0.2)",
              borderRadius: "8px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
              boxShadow: "0 0 10px rgba(0,0,0,0.5)",
              transition: "transform 0.2s, border-color 0.2s"
            }}
          >
            <div style={{ padding: "12px", background: "#0d0f14", borderRadius: "8px" }}>
              {item.component}
            </div>
            <strong style={{ fontSize: "14px", color: "#f0f6fc", fontFamily: "monospace" }}>{item.name}</strong>
            <span style={{ fontSize: "11px", color: "#00f3ff", background: "rgba(0,243,255,0.1)", padding: "2px 8px", borderRadius: "4px" }}>
              {item.tag}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CyberpunkIconShowcase;
